import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';
import { aboutPageSchema } from '@/mocks/schema/about';

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <title>关于我们 - 2pg·com 品牌介绍与联系方式</title>
      <meta
        name="description"
        content="了解2pg·com的品牌故事、平台优势、信誉保障以及联系方式。我们致力于为每一位用户提供安全、便捷、丰富的游戏娱乐体验。"
      />
      <link rel="canonical" href="https://2pg.com/about" />
      <SchemaOrg schemas={aboutPageSchema} />

      <Navbar />
      <main className="pt-16">
        <section className="relative h-[280px] md:h-[320px] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20premium%20corporate%20office%20interior%20with%20sleek%20design%20and%20warm%20natural%20lighting%20through%20large%20windows%2C%20professional%20team%20environment%20aesthetic%2C%20clean%20minimal%20architecture%2C%20sophisticated%20business%20atmosphere%20with%20subtle%20luxury%20details&width=1600&height=500&seq=about-hero&orientation=landscape"
              alt="关于2pg·com"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/60"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 pb-10">
            <nav className="text-sm mb-3" aria-label="面包屑导航">
              <ol className="flex items-center gap-2 text-white/70">
                <li itemScope itemType="https://schema.org/ListItem">
                  <Link itemProp="item" to="/" className="hover:text-white transition-colors cursor-pointer">
                    <span itemProp="name">首页</span>
                  </Link>
                  <meta itemProp="position" content="1" />
                </li>
                <li className="text-white/40">/</li>
                <li itemScope itemType="https://schema.org/ListItem" className="text-white">
                  <span itemProp="name">关于我们</span>
                  <meta itemProp="position" content="2" />
                </li>
              </ol>
            </nav>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-white">关于我们</h1>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block text-sm font-semibold text-primary-500 mb-3">品牌介绍</span>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4">
                值得信赖的游戏娱乐平台
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed">
                2pg·com 自成立以来，始终秉承"简单、安全、畅玩"的核心价值观，致力于为全球用户提供一流的游戏娱乐体验。我们的平台以用户为中心，不断优化产品与服务，让每一位玩家都能在这里找到属于自己的快乐。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <div className="text-center p-6 bg-background-100 rounded-2xl">
                <div className="text-3xl md:text-4xl font-bold font-heading text-primary-500 mb-2">1000万+</div>
                <p className="text-sm text-foreground-600">注册用户数</p>
              </div>
              <div className="text-center p-6 bg-background-100 rounded-2xl">
                <div className="text-3xl md:text-4xl font-bold font-heading text-accent-500 mb-2">8年</div>
                <p className="text-sm text-foreground-600">稳定运营时长</p>
              </div>
              <div className="text-center p-6 bg-background-100 rounded-2xl">
                <div className="text-3xl md:text-4xl font-bold font-heading text-secondary-500 mb-2">200+</div>
                <p className="text-sm text-foreground-600">游戏产品数量</p>
              </div>
            </div>

            <div className="mb-16">
              <span className="inline-block text-sm font-semibold text-primary-500 mb-3">平台优势</span>
              <h3 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">为什么选择 2pg·com</h3>
              <div className="flex flex-col gap-6">
                <div className="flex gap-4 p-5 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-shield-check-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground-900 mb-1">安全保障</h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">采用国际顶级加密技术，多重安全防护体系，通过多项安全认证，用户数据和资金安全始终是我们的首要任务。</p>
                  </div>
                </div>
                <div className="flex gap-4 p-5 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-lightbulb-flash-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground-900 mb-1">技术创新</h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">自主研发高性能游戏引擎，确保流畅稳定的游戏体验。持续引入前沿技术，为玩家打造更沉浸、更有趣的娱乐世界。</p>
                  </div>
                </div>
                <div className="flex gap-4 p-5 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-customer-service-2-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground-900 mb-1">专业服务</h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">7×24小时专业客服团队在线响应，多渠道支持，确保每位用户的问题都能得到及时、妥善的解决。</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-16">
              <span className="inline-block text-sm font-semibold text-primary-500 mb-3">平台信誉</span>
              <h3 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">多年口碑，值得信赖</h3>
              <p className="text-base text-foreground-700 leading-relaxed mb-5">
                2pg·com 已稳定运营超过八年，累计注册用户突破千万大关。我们获得了国际权威机构的合规认证，严格遵守运营所在地的各项法律法规。平台资金流转透明可查，提现到账快速稳定，赢得了广大用户的一致好评。
              </p>
              <p className="text-base text-foreground-700 leading-relaxed">
                凭借出色的产品品质和优质的用户服务，2pg·com 多次荣获行业奖项，被多家权威媒体和评测机构评为"年度最佳游戏平台"、"最受信赖娱乐品牌"等荣誉称号。这些认可激励着我们不断前行，为用户创造更大的价值。
              </p>
            </div>

            <div>
              <span className="inline-block text-sm font-semibold text-primary-500 mb-3">联系我们</span>
              <h3 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">期待与您沟通</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="p-5 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h4 className="font-bold text-foreground-900 mb-1">客服邮箱</h4>
                  <p className="text-sm text-foreground-600">support@2pg.com</p>
                </div>
                <div className="p-5 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                    <i className="ri-customer-service-line text-lg"></i>
                  </div>
                  <h4 className="font-bold text-foreground-900 mb-1">在线客服</h4>
                  <p className="text-sm text-foreground-600">7×24小时实时在线</p>
                </div>
                <div className="p-5 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-3">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <h4 className="font-bold text-foreground-900 mb-1">服务时间</h4>
                  <p className="text-sm text-foreground-600">全天候不间断服务</p>
                </div>
                <div className="p-5 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-feedback-line text-lg"></i>
                  </div>
                  <h4 className="font-bold text-foreground-900 mb-1">意见反馈</h4>
                  <p className="text-sm text-foreground-600">feedback@2pg.com</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}